import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { BarChart3, FileText, ShieldCheck } from "lucide-react";

export const CURRENT_TERMS_VERSION = "2026-06";

interface TermsGateProps {
  onAccepted: () => void;
}

export function TermsGate({ onAccepted }: TermsGateProps) {
  const { t } = useTranslation(["nav", "common"]);
  const [checked, setChecked] = useState(false);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/auth/accept-terms", { version: CURRENT_TERMS_VERSION }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["/api/auth/me"] });
      onAccepted();
    },
  });

  return (
    <div className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl border bg-card shadow-xl">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-6 pb-4 border-b">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shrink-0">
            <BarChart3 className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <p className="font-bold text-base leading-tight">DealerMind</p>
            <p className="text-xs text-muted-foreground">{t("nav:termsGate.update")}</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-3 rounded-lg border bg-muted/40 p-4">
            <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-semibold">{t("nav:termsGate.updatedHeading")}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t("nav:termsGate.updatedBody")}
              </p>
            </div>
          </div>

          <div className="rounded-lg border bg-muted/20 px-4 py-3 space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t("nav:termsGate.keyChanges")}</p>
            <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
              <li>{t("nav:termsGate.change1")}</li>
              <li>{t("nav:termsGate.change2")}</li>
              <li>{t("nav:termsGate.change3")}</li>
              <li>{t("nav:termsGate.change4")}</li>
            </ul>
          </div>

          <div
            className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => setChecked((v) => !v)}
            data-testid="terms-gate-checkbox-area"
          >
            <Checkbox
              id="terms-gate-accept"
              checked={checked}
              onCheckedChange={(v) => setChecked(!!v)}
              data-testid="checkbox-terms-accept"
              className="mt-0.5 shrink-0"
            />
            <Label htmlFor="terms-gate-accept" className="text-xs leading-relaxed cursor-pointer">
              {t("nav:termsGate.agreePrefix")}{" "}
              <Link
                href="/terms"
                target="_blank"
                className="text-primary underline underline-offset-2 hover:no-underline"
                onClick={(e) => e.stopPropagation()}
                data-testid="link-terms-gate-view"
              >
                {t("nav:termsGate.termsOfService")}
              </Link>
              {" "}{t("nav:termsGate.effective")}
            </Label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex flex-col gap-2">
          <Button
            className="w-full"
            disabled={!checked || mutation.isPending}
            onClick={() => mutation.mutate()}
            data-testid="button-terms-gate-accept"
          >
            {mutation.isPending ? t("nav:termsGate.saving") : t("nav:termsGate.acceptContinue")}
          </Button>
          {mutation.isError && (
            <p className="text-xs text-destructive text-center" data-testid="text-terms-gate-error">
              {t("nav:termsGate.error")}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground text-center leading-tight">
            {t("nav:termsGate.footer")}{" "}
            <a href="mailto:dealermindai.net@gmail.com" className="underline">{t("nav:termsGate.support")}</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
